import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { MessageSquare, Loader2, Users, Sparkles, Search, X, SlidersHorizontal, BookOpen, ChevronDown, ChevronUp, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { api } from "@/lib/api";
import { cn, displayName } from "@/lib/utils";
import { ATTENDEE_PARTICIPANT_TYPES } from "@/lib/constants";
import { useDismissedRecommendations } from "@/hooks/useDismissedRecommendations";
import { useDevice } from "@/hooks/useDeviceType";
import { useActiveEvent } from "@/hooks/useActiveEvent";
import { OfflineDataBanner } from "@/components/OfflineDataBanner";

// Lazy load desktop version
const ConnectionsDesktopPage = lazy(() => import('./ConnectionsPage.desktop'));

function ConnectionsSkeleton() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

interface RecommendedConnection {
  id: string;
  full_name: string;
  role: string;
  organization: string;
  acceleration_interests: string[];
  score: number;
  sharedInterests: string[];
  mutualConnections: number;
  sameOrganization: boolean;
  avatarUrl?: string;
}

interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  organization?: string;
  department?: string;
  role?: string;
  avatarUrl?: string;
  accelerationInterests?: string[];
  isCheckedIn?: boolean;
  isExhibitor?: boolean;
  participantType?: string;
  isFavorited?: boolean;
  bio?: string;
}


export default function ConnectionsPage() {
  const { isDesktop } = useDevice();

  // Render desktop version for desktop users
  if (isDesktop) {
    return (
      <Suspense fallback={<ConnectionsSkeleton />}>
        <ConnectionsDesktopPage />
      </Suspense>
    );
  }

  // Mobile/Tablet version
  return <ConnectionsMobilePage />;
}

function ConnectionsMobilePage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("favorites");
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    participantTypes: string[];
    sortBy: string;
    eventAttendeesOnly: boolean;
  }>({
    participantTypes: [],
    sortBy: 'recent',
    eventAttendeesOnly: false
  });
  const [recommendations, setRecommendations] = useState<RecommendedConnection[]>([]);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [favoritingIds, setFavoritingIds] = useState<Set<string>>(new Set());
  const { dismiss: dismissRecommendation, isDismissed: isRecommendationDismissed } = useDismissedRecommendations('connections');
  const { event: activeEvent } = useActiveEvent();

  const navigate = useNavigate();

  useEffect(() => {
    if (activeEvent?.id) {
      fetchParticipants();
    }
    fetchFavorites();
  }, [activeEvent?.id]);

  useEffect(() => {
    fetchRecommendations();
  }, [favorites]);

  const [privateProfileMatch, setPrivateProfileMatch] = useState(false);

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/users/favorites');
      const data = (response as any).data || [];
      const favoriteIds = new Set<string>(data.map((f: any) => f.id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchParticipants = async (): Promise<Participant[]> => {
    setParticipantsLoading(true);
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '10000' });
      if (activeEvent?.id) params.set('eventId', activeEvent.id);
      const response = await api.get(`/users/participants?${params}`);
      const data = (response as any).data || [];
      setPrivateProfileMatch((response as any).privateProfileMatch || false);
      const mapped: Participant[] = data.map((p: any) => ({
        id: p.id,
        firstName: p.firstName || p.first_name || '',
        lastName: p.lastName || p.last_name || '',
        organization: p.organization,
        department: p.department,
        role: p.role,
        avatarUrl: p.avatarUrl,
        accelerationInterests: p.accelerationInterests || p.acceleration_interests || [],
        isCheckedIn: p.isCheckedIn || p.is_checked_in || false,
        isExhibitor: p.isExhibitor === true,
        participantType: p.participantType || p.participant_type || null,
        bio: p.bio,
      }));
      setParticipants(mapped);
      return mapped;
    } catch (error) {
      console.error('Error fetching participants:', error);
      return [];
    } finally {
      setParticipantsLoading(false);
      setLoading(false);
    }
  };

  const toggleFavorite = async (userId: string) => {
    if (favoritingIds.has(userId)) return;

    const currentlyFavorited = favorites.has(userId);
    setFavoritingIds(prev => new Set(prev).add(userId));

    try {
      if (currentlyFavorited) {
        await api.delete(`/users/${userId}/favorite`);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        toast.success('Removed from My Connections');
      } else {
        await api.post(`/users/${userId}/favorite`);
        setFavorites(prev => new Set(prev).add(userId));
        toast.success('Added to My Connections');
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || 'Failed to update favorite';
      toast.error(message);
    } finally {
      setFavoritingIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await api.get('/connections/recommendations');
      const data = (response as any).data || [];
      const mapped = data.map((rec: any) => ({
        id: rec.id,
        full_name: rec.full_name || `${rec.firstName || ''} ${rec.lastName || ''}`.trim() || 'Unknown',
        role: rec.role || '',
        organization: rec.organization || '',
        acceleration_interests: rec.accelerationInterests || rec.acceleration_interests || [],
        score: rec.matchScore || rec.score || 0,
        sharedInterests: rec.matchReasons || rec.sharedInterests || [],
        mutualConnections: rec.mutualConnections || 0,
        sameOrganization: rec.sameOrganization || false,
        avatarUrl: rec.avatarUrl || undefined,
      }));
      setRecommendations(mapped.slice(0, 3));
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  };

  // Filter participants who are favorited for "My Connections" tab
  const favoritedParticipants = useMemo(() => {
    return participants.filter(p => favorites.has(p.id));
  }, [participants, favorites]);

  const filteredFavorites = useMemo(() => {
    let result = [...favoritedParticipants];

    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      result = result.filter(p => {
        const nameMatch = displayName(p).toLowerCase().includes(searchLower);
        const orgMatch = p.organization?.toLowerCase().includes(searchLower);
        return nameMatch || orgMatch;
      });
    }

    if (filters.participantTypes.length > 0) {
      result = result.filter(p => {
        const pType = p.participantType?.toLowerCase();
        return filters.participantTypes.some((type: string) => pType === type);
      });
    }

    if (filters.eventAttendeesOnly) {
      result = result.filter(p => p.isCheckedIn === true);
    }

    switch (filters.sortBy) {
      case 'name-asc':
        result.sort((a, b) => displayName(a).localeCompare(displayName(b)));
        break;
      case 'name-desc':
        result.sort((a, b) => displayName(b).localeCompare(displayName(a)));
        break;
    }

    return result;
  }, [favoritedParticipants, searchQuery, filters]);

  const filteredParticipants = useMemo(() => {
    let result = [...participants];

    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      result = result.filter(p => {
        const nameMatch = displayName(p).toLowerCase().includes(searchLower);
        const orgMatch = p.organization?.toLowerCase().includes(searchLower);
        return nameMatch || orgMatch;
      });
    }

    if (filters.participantTypes.length > 0) {
      result = result.filter(p => {
        const pType = p.participantType?.toLowerCase();
        return filters.participantTypes.some((type: string) => pType === type);
      });
    }

    if (filters.eventAttendeesOnly) {
      result = result.filter(p => p.isCheckedIn === true);
    }

    switch (filters.sortBy) {
      case 'name-asc':
        result.sort((a, b) => displayName(a).localeCompare(displayName(b)));
        break;
      case 'name-desc':
        result.sort((a, b) => displayName(b).localeCompare(displayName(a)));
        break;
    }

    return result;
  }, [participants, searchQuery, filters]);

  const handleStartMessage = async (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    try {
      await api.post('/messages/conversations', { recipientId: userId });
      navigate('/messages');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start conversation';
      toast.error(message);
    }
  };

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const clearFilters = () => {
    setFilters({ participantTypes: [], sortBy: 'recent', eventAttendeesOnly: false });
    setSearchQuery('');
  };

  const activeFilterCount = filters.participantTypes.length + (filters.eventAttendeesOnly ? 1 : 0);

  const renderParticipantCard = (participant: Participant, keyPrefix: string = '') => {
    const isFavorited = favorites.has(participant.id);
    const isFavoriting = favoritingIds.has(participant.id);
    const cardKey = `${keyPrefix}${participant.id}`;

    return (
      <Card key={cardKey} className="p-4 md:p-5 shadow-md border-border/50 hover:shadow-lg transition-all duration-300">
        {/* Collapsed View */}
        <div className="space-y-3">
          <div className="flex items-start gap-3 md:gap-4">
            {/* Avatar */}
            <Avatar className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0">
              <AvatarImage src={participant.avatarUrl || undefined} />
              <AvatarFallback className="bg-gradient-navy text-primary-foreground font-semibold text-sm md:text-base">
                {displayName(participant).split(' ').map(n => n[0]).join('').slice(0, 2)}
              </AvatarFallback>
            </Avatar>

            {/* Main Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-semibold text-foreground text-base md:text-lg">
                  {displayName(participant)}
                </h3>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Exhibitor Badge (booth at this event) */}
                  {participant.isExhibitor && (
                    <Badge variant="default" className="text-[10px] px-1.5 py-0.5 h-6 bg-teal-600 hover:bg-teal-700 text-white flex items-center">
                      Exhibitor
                    </Badge>
                  )}
                  {/* At Event Badge */}
                  {participant.isCheckedIn && (
                    <Badge variant="default" className="text-[10px] px-1.5 py-0.5 h-6 bg-blue-500 hover:bg-blue-600 flex items-center">
                      <MapPin className="h-3 w-3 mr-0.5" />
                      At Event
                    </Badge>
                  )}
                  {/* Star Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-10 w-10 shrink-0",
                      isFavorited ? "text-yellow-500 hover:text-yellow-600" : "text-muted-foreground hover:text-yellow-500"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(participant.id);
                    }}
                    disabled={isFavoriting}
                  >
                    {isFavoriting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Star className={cn("h-5 w-5", isFavorited && "fill-current")} />
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-sm md:text-sm text-muted-foreground">
                {participant.role || 'Role not specified'}
                {participant.organization && ` • ${participant.organization}`}
              </p>
            </div>
          </div>

          {/* Expand/Collapse Control */}
          <Button
            variant="outline"
            className="w-full h-8 text-xs text-muted-foreground [@media(hover:hover)]:hover:text-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              toggleCardExpansion(cardKey);
            }}
          >
            {expandedCards.has(cardKey) ? (
              <>
                <span>Show Less</span>
                <ChevronUp className="h-4 w-4 ml-1" />
              </>
            ) : (
              <>
                <span>Show More Details</span>
                <ChevronDown className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>

        {/* Expanded View */}
        {expandedCards.has(cardKey) && (
          <div className="mt-4 pt-4 border-t border-border space-y-4" onClick={(e) => e.stopPropagation()}>
            {/* Organization */}
            {participant.organization && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Organization</p>
                <p className="text-sm text-foreground">{participant.organization}</p>
              </div>
            )}

            {/* Department */}
            {participant.department && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Department</p>
                <p className="text-sm text-foreground">{participant.department}</p>
              </div>
            )}

            {/* Participant Type */}
            {participant.participantType && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Participant Type</p>
                <p className="text-sm text-foreground">{ATTENDEE_PARTICIPANT_TYPES.find(t => t.value === participant.participantType)?.label ?? participant.participantType}</p>
              </div>
            )}

            {/* Bio */}
            {participant.bio && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Bio</p>
                <p className="text-sm text-foreground leading-relaxed">{participant.bio}</p>
              </div>
            )}

            {/* Technology Interests */}
            {participant.accelerationInterests && participant.accelerationInterests.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">Technology Interests</p>
                <div className="flex flex-wrap gap-2">
                  {participant.accelerationInterests.map((interest) => (
                    <Badge key={interest} variant="secondary" className="text-xs">
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Message Button - Bottom of card */}
        <div className="mt-3 pt-3 border-t border-border/30">
          <Button
            className="w-full gap-2 h-10"
            onClick={(e) => handleStartMessage(e, participant.id)}
          >
            <MessageSquare className="h-4 w-4" />
            Message
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-subtle pb-24">
      <PageHeader
        title="My Network"
        subtitle={`${favorites.size} connections made`}
      />

      <main className="px-3 md:px-4 pt-3 md:pt-4 space-y-3 md:space-y-4">
        <OfflineDataBanner />

        {/* Search Bar */}
        <Card className="p-3 md:p-4 shadow-md border-accent/20 bg-gradient-to-br from-background to-secondary/20">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 md:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-4 md:h-4 text-accent" />
              <Input
                placeholder="Search by name or organization..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 md:pl-10 pr-10 md:pr-10 h-11 md:h-11 text-sm md:text-base border-border/50 bg-background/50 focus:bg-background transition-colors"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="w-4 h-4 md:w-4 md:h-4" />
                </Button>
              )}
            </div>

            <div className="flex gap-2 md:gap-2">
              <Button
                variant={showFilters ? "default" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "flex-1 sm:flex-none h-11 md:h-11 px-4 md:px-4 text-sm md:text-sm transition-colors",
                  showFilters
                    ? "bg-primary text-primary-foreground [@media(hover:hover)]:hover:bg-primary/90"
                    : "bg-background border-primary/30 text-foreground [@media(hover:hover)]:hover:bg-primary/10 [@media(hover:hover)]:hover:text-foreground active:bg-primary active:text-primary-foreground"
                )}
              >
                <SlidersHorizontal className="w-4 h-4 md:w-4 md:h-4 mr-2 md:mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="default" className={cn(
                    "ml-2 md:ml-2 h-5 w-5 md:h-5 md:w-5 p-0 flex items-center justify-center text-xs",
                    showFilters ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"
                  )}>
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>

              <Select value={filters.sortBy} onValueChange={(value) => setFilters(prev => ({ ...prev, sortBy: value }))}>
                <SelectTrigger className="flex-1 sm:w-[160px] h-11 md:h-11 text-sm md:text-sm border-accent/30">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Search Results Count */}
        {(searchQuery || activeFilterCount > 0) && (
          <p className="text-sm text-muted-foreground">
            {activeTab === 'participants'
              ? `${filteredParticipants.length} result${filteredParticipants.length !== 1 ? 's' : ''} found`
              : `${filteredFavorites.length} result${filteredFavorites.length !== 1 ? 's' : ''} found`
            }
          </p>
        )}

        {/* Collapsible Filter Panel */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent>
            <Card className="p-3 md:p-4 space-y-3 md:space-y-4">
              {/* At Event Filter */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="event-attendees-only"
                  checked={filters.eventAttendeesOnly}
                  onCheckedChange={(checked) => {
                    setFilters(prev => ({
                      ...prev,
                      eventAttendeesOnly: checked === true
                    }));
                  }}
                  className="h-4 w-4"
                />
                <Label htmlFor="event-attendees-only" className="cursor-pointer text-xs md:text-sm font-medium">
                  At Event
                </Label>
              </div>

              {/* Participant Type Filter */}
              <div className="space-y-1.5 md:space-y-2 pt-2 border-t border-border/50">
                <Label className="text-xs md:text-sm font-semibold">Filter by Participant Type</Label>
                <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                  {ATTENDEE_PARTICIPANT_TYPES.map(type => (
                    <div key={type.value} className="flex items-center space-x-1.5 md:space-x-2">
                      <Checkbox
                        id={`type-${type.value}`}
                        checked={filters.participantTypes.includes(type.value)}
                        onCheckedChange={(checked) => {
                          setFilters(prev => ({
                            ...prev,
                            participantTypes: checked
                              ? [...prev.participantTypes, type.value]
                              : prev.participantTypes.filter((t: string) => t !== type.value)
                          }));
                        }}
                        className="h-3.5 w-3.5 md:h-4 md:w-4"
                      />
                      <Label htmlFor={`type-${type.value}`} className="cursor-pointer text-xs md:text-sm">
                        {type.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {activeFilterCount > 0 && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full h-8 md:h-9 text-xs md:text-sm"
                  size="sm"
                >
                  Clear All Filters
                </Button>
              )}
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground">Active:</span>
            {filters.participantTypes.map(type => {
              const typeLabel = ATTENDEE_PARTICIPANT_TYPES.find(t => t.value === type)?.label;
              return (
                <Badge key={type} variant="secondary" className="gap-1 pr-1">
                  {typeLabel}
                  <button
                    className="ml-1 [@media(hover:hover)]:hover:bg-muted rounded-full p-0.5"
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      participantTypes: prev.participantTypes.filter((t: string) => t !== type)
                    }))}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
            {filters.eventAttendeesOnly && (
              <Badge variant="secondary" className="gap-1 pr-1">
                At Event
                <button
                  className="ml-1 [@media(hover:hover)]:hover:bg-muted rounded-full p-0.5"
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    eventAttendeesOnly: false
                  }))}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-6 text-xs"
            >
              Clear All
            </Button>
          </div>
        )}

        {/* Inline Recommendations */}
        {activeTab === 'participants' && recommendations.filter(r => !isRecommendationDismissed(r.id) && !favorites.has(r.id)).length > 0 && (
          <Card className="p-3 md:p-4 bg-gradient-to-br from-accent/5 to-primary/5 border-accent/30">
            <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3">
              <Sparkles className="h-3.5 w-3.5 md:h-4 md:w-4 text-accent" />
              <h3 className="font-semibold text-xs md:text-sm">Suggested Connections</h3>
            </div>
            <div className="space-y-2 md:space-y-3">
              {recommendations.filter(r => !isRecommendationDismissed(r.id) && !favorites.has(r.id)).map((rec) => (
                <div key={rec.id} className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-background/50 rounded-lg hover:shadow-sm transition-shadow relative">
                  <Avatar className="h-8 w-8 md:h-10 md:w-10 shrink-0">
                    <AvatarImage src={rec.avatarUrl || undefined} />
                    <AvatarFallback className="bg-gradient-navy text-primary-foreground text-xs md:text-sm">
                      {rec.full_name.split(' ').map((n: string) => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 overflow-hidden pr-5 md:pr-6">
                    <p className="font-medium text-xs md:text-sm truncate" title={rec.full_name}>{rec.full_name}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground truncate" title={`${rec.role} • ${rec.organization}`}>
                      {rec.role} • {rec.organization}
                    </p>
                    <div className="flex items-center gap-2 md:gap-3 mt-0.5 md:mt-1 flex-wrap">
                      {rec.sharedInterests.length > 0 && (
                        <div className="flex items-center gap-1">
                          <BookOpen className="h-2.5 w-2.5 md:h-3 md:w-3 text-accent shrink-0" />
                          <span className="text-[10px] md:text-xs text-muted-foreground">
                            {rec.sharedInterests.length} shared
                          </span>
                        </div>
                      )}
                      {rec.mutualConnections > 0 && (
                        <div className="flex items-center gap-1">
                          <Users className="h-2.5 w-2.5 md:h-3 md:w-3 text-primary shrink-0" />
                          <span className="text-[10px] md:text-xs text-muted-foreground">
                            {rec.mutualConnections} mutual
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "shrink-0 h-8 w-8",
                      favorites.has(rec.id) ? "text-yellow-500" : "text-muted-foreground hover:text-yellow-500"
                    )}
                    onClick={() => toggleFavorite(rec.id)}
                    disabled={favoritingIds.has(rec.id)}
                  >
                    {favoritingIds.has(rec.id) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Star className={cn("h-4 w-4", favorites.has(rec.id) && "fill-current")} />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-4 w-4 md:h-5 md:w-5 text-muted-foreground [@media(hover:hover)]:hover:text-foreground [@media(hover:hover)]:hover:bg-muted"
                    onClick={(e) => { e.stopPropagation(); dismissRecommendation(rec.id); }}
                    aria-label="Dismiss recommendation"
                  >
                    <X className="h-2.5 w-2.5 md:h-3 md:w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 h-8 md:h-10">
            <TabsTrigger value="favorites" className="text-xs md:text-sm">
              My Connections
              <Badge variant="secondary" className="ml-1.5 text-[10px] md:text-xs px-1.5 py-0">
                {filteredFavorites.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="participants" className="text-xs md:text-sm">
              <Users className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1" />
              Converge Community
              <Badge variant="secondary" className="ml-1.5 text-[10px] md:text-xs px-1.5 py-0">
                {filteredParticipants.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Content List */}
        <div className="space-y-2.5 md:space-y-4">
          {/* My Connections (Favorites) Tab Content */}
          {activeTab === 'favorites' && (
            loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i} className="p-4 md:p-5">
                    <div className="flex items-start gap-3 md:gap-4">
                      <Skeleton className="w-12 h-12 md:w-14 md:h-14 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-10 w-10 rounded-md" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredFavorites.length === 0 ? (
              <Card className="p-6 md:p-12 text-center">
                <div className="w-14 h-14 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 bg-secondary/50 rounded-full flex items-center justify-center">
                  <Star className="h-7 w-7 md:h-10 md:w-10 text-muted-foreground" />
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-foreground mb-1.5 md:mb-2">
                  {searchQuery || activeFilterCount > 0
                    ? 'No Matching Connections'
                    : 'No Connections Yet'}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-4 md:mb-6">
                  {searchQuery || activeFilterCount > 0
                    ? 'Try adjusting your search or filters'
                    : 'Star people in the Converge Community tab to add them to your connections'
                  }
                </p>
                {!searchQuery && activeFilterCount === 0 && (
                  <Button
                    className="gap-2 h-11 md:h-10 text-sm md:text-base"
                    onClick={() => setActiveTab('participants')}
                  >
                    <Users className="h-4 w-4 md:h-5 md:w-5" />
                    Browse Community
                  </Button>
                )}
              </Card>
            ) : (
              filteredFavorites.map((participant) => renderParticipantCard(participant, 'fav-'))
            )
          )}

          {/* Converge Community Tab Content */}
          {activeTab === 'participants' && (
            participantsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i} className="p-4 md:p-5">
                    <div className="flex items-start gap-3 md:gap-4">
                      <Skeleton className="w-12 h-12 md:w-14 md:h-14 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-10 w-10 rounded-md" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredParticipants.length === 0 ? (
              <Card className="p-6 md:p-12 text-center">
                <div className="w-14 h-14 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 bg-secondary/50 rounded-full flex items-center justify-center">
                  <Users className="h-7 w-7 md:h-10 md:w-10 text-muted-foreground" />
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-foreground mb-1.5 md:mb-2">
                  {searchQuery || filters.participantTypes.length > 0
                    ? 'No Matching Participants'
                    : 'No Participants Yet'}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground mb-4 md:mb-6">
                  {searchQuery || filters.participantTypes.length > 0
                    ? 'Try adjusting your search or filters'
                    : 'Participants will appear here as they register for the event'}
                </p>
                {/* Private Profile Placeholder */}
                {privateProfileMatch && searchQuery && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-xs md:text-sm text-amber-800 dark:text-amber-200">
                      A participant matching "{searchQuery}" has a private profile.
                    </p>
                  </div>
                )}
              </Card>
            ) : (
              <>
                {/* Private Profile Notice */}
                {privateProfileMatch && searchQuery && (
                  <Card className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <p className="text-xs md:text-sm text-amber-800 dark:text-amber-200">
                      A participant matching "{searchQuery}" has a private profile.
                    </p>
                  </Card>
                )}
                {filteredParticipants.map((participant) => renderParticipantCard(participant, 'community-'))}
              </>
            )
          )}
        </div>
      </main>
    </div>
  );
}
